import { QuestionRadiogroupModel, ItemValue } from "survey-core";
import { useMemo } from "react";
import { createStyles } from "antd-style";
import { glassLocationCardSvgs } from "./glassLocationCardSvgs";
import { glassDamageExtentSvgs } from "./glassDamageExtentSvgs";
import { glassWindscreenPositionSvgs } from "./glassWindscreenPositionSvgs";
import { glassChipCountSvgs } from "./glassChipCountSvgs";
import { glassDriverVisionSizeSvgs } from "./glassDriverVisionSizeSvgs";
import { glassSideRearLocationSvgs } from "./glassSideRearLocationSvgs";

// Raw (non-theme-remapped) illustrations, looked up alongside the themed
// location SVGs. Keep these separate so the st0-st3 theming only applies to the
// simple line-art location cards.
const rawCardSvgs: Record<string, string> = {
  ...glassDamageExtentSvgs,
  ...glassWindscreenPositionSvgs,
  ...glassChipCountSvgs,
  ...glassDriverVisionSizeSvgs,
  ...glassSideRearLocationSvgs,
};

export class RadioCardQuestionModel extends QuestionRadiogroupModel {
  getType() {
    return "radioCard";
  }
}

const useStyles = createStyles(({ css, token }) => ({
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 15px;
  `,
  item: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    padding: 16px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: 0 0 0 1px ${token.colorPrimary};
    }

    &:focus-visible {
      outline: none;
      border-color: ${token.colorPrimary};
      box-shadow: 0 0 0 2px ${token.colorPrimary};
    }
  `,
  itemSelected: css`
    border-color: ${token.colorPrimary};
    background-color: ${token.colorPrimaryBg};
    box-shadow: 0 0 0 1px ${token.colorPrimary};
  `,
  itemDisabled: css`
    cursor: not-allowed;
    opacity: 0.6;
  `,
  image: css`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: auto;
      height: 140px;
      max-width: 100%;
    }
  `,
  /* The source illustrations use class fills st0-st3; remap them to theme
     tokens so the highlighted pane adapts to the affiliate primary color.
     Only applied to the themeable line-art illustrations, never to the
     realistic gradient illustrations of the damage-extent step. */
  imageThemed: css`
    svg .st0 {
      fill: none;
      stroke: ${token.colorText};
    }
    svg .st1 {
      fill: ${token.colorPrimary};
    }
    svg .st2 {
      fill: ${token.colorFillTertiary};
    }
    svg .st3 {
      fill: ${token.colorText};
    }
  `,
  /* Landscape illustrations would otherwise span the full card width; cap their
     size and add horizontal breathing room. */
  imageWide: css`
    padding: 8px 16px;

    svg {
      height: 96px;
    }
  `,
  text: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    margin-top: auto;
  `,
  label: css`
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    line-height: 1.4;
  `,
  description: css`
    font-size: 12px;
    color: ${token.colorTextSecondary};
    text-align: center;
    line-height: 1.3;
  `,
}));

const isWideSvg = (svg?: string): boolean => {
  const match = svg?.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
  if (!match) return false;
  return parseFloat(match[1]) > parseFloat(match[2]);
};

export const RadioCardQuestion = (props: {
  question: RadioCardQuestionModel;
}) => {
  const { styles, cx } = useStyles();
  const question = props.question;

  const selectedValue = useMemo(() => question.value, [question.value]);

  const handleItemClick = (item: ItemValue) => {
    if (question.isDisabledAttr || question.isReadOnly) return;
    question.value = item.value;
  };

  const isDisabled = question.isDisabledAttr || question.isReadOnly;

  return (
    <div
      className={styles.grid}
      role="radiogroup"
      aria-label={question.title ?? question.name}
    >
      {question.visibleChoices.map((choice: ItemValue) => {
        const isSelected = selectedValue === choice.value;
        const themedSvg = glassLocationCardSvgs[choice.value];
        const rawSvg = rawCardSvgs[choice.value];
        const svg = themedSvg ?? rawSvg;
        const isThemed = !!themedSvg;
        const description = choice.getPropertyValue?.("textExtra");

        return (
          <div
            key={choice.value}
            className={cx(
              styles.item,
              isSelected && styles.itemSelected,
              isDisabled && styles.itemDisabled,
            )}
            onClick={() => handleItemClick(choice)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleItemClick(choice);
              }
            }}
            role="radio"
            aria-checked={isSelected}
            aria-label={choice.text}
            aria-disabled={isDisabled || undefined}
            tabIndex={isDisabled ? -1 : 0}
          >
            {svg && (
              <div
                className={cx(
                  styles.image,
                  isThemed && styles.imageThemed,
                  isWideSvg(svg) && styles.imageWide,
                )}
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            )}
            <div className={styles.text}>
              <div className={styles.label}>{choice.text}</div>
              {description && (
                <div className={styles.description}>{description}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
